import React, { useEffect, useState } from 'react'
import {
  AdoptionAttemptDto,
  AdoptionAttemptDtoStatus,
  AnimalDto,
  AnimalInsightsDtoType,
} from '../../../interactors/gen/backendClient'
import { Card, CardContent, Box, Avatar, Typography, AvatarGroup, Button } from '@mui/material'
import { Link, useNavigate } from 'react-router-dom'
import { getAnimalProfilePhotoSrc } from '../../../utils/S3-links'
import { blueSky, colors } from '../../theme'
import { AnimalsClient } from '../../../interactors/clients/AnimalsClient'
import { getReadableSterilizedForFerrets, getReadableSpecies } from '../../../domain/Animal/AnimalDisplay'
import { AnimalDtoSpecies } from '../../../interactors/gen/backendClient'
import { getAdoptionAttemptStatus } from '../../../utils/getAdoptionAttemptStatus'
import { CircleWithNumber, HtmlTooltip } from '../../Animals/AnimalsTableItem'
import { InfoBox } from '../../common/InfoBox'
import { getSoftChipSx } from '../../common/chipStyles'
import { addSecondaryFabSx } from '../../common/addPrimaryCtaSx'
import { getGuestCheckout, getGuestCheckoutUrl, isGuestCheckoutExpired } from '../../../domain/Adopters/GuestAdoption'
import { useGlobalSnackbarStore } from '../../../store/GlobalSnackBarStore'
import { GuestAdoptionChip } from '../GuestAdoption/GuestAdoptionChip'

interface Props {
  adoptionAttempt: AdoptionAttemptDto
}

export const OnGoingAdoptionItem: React.FC<Props> = ({ adoptionAttempt }) => {
  const [animal, setAnimal] = useState<AnimalDto | undefined>(undefined)
  const animalId = adoptionAttempt.animalId
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(window.location.search)
  const tab = queryParams.get('tab')
  const globalSnackBarStore = useGlobalSnackbarStore()
  const guestCheckout = getGuestCheckout(adoptionAttempt)

  // Fetch animal data
  useEffect(() => {
    const fetchAnimal = async () => {
      const animal = await AnimalsClient.getAnimal(animalId)
      if (animal) {
        setAnimal(animal)
      }
    }

    fetchAnimal()
  }, [animalId])

  const handleCopyLink = () => {
    if (adoptionAttempt.paymentLink) {
      navigator.clipboard.writeText(adoptionAttempt.paymentLink)
      alert('Lien de paiement copié dans le presse-papiers.')
    }
  }

  const handleCopyGuestCheckoutLink = () => {
    if (guestCheckout) {
      navigator.clipboard.writeText(getGuestCheckoutUrl(guestCheckout.token))
      globalSnackBarStore.triggerSuccessMessage('Lien de paiement copié dans le presse-papier.')
    }
  }

  const profileImageKey = animal?.images?.profileImageKey
  const animalProperties = [
    animal?.species && getReadableSpecies(animal?.species),
    animal?.species === AnimalDtoSpecies.Ferret && animal?.health.sterilizedForFerrets
      ? getReadableSterilizedForFerrets(animal.health.sterilizedForFerrets)
      : animal?.health.sterilizedOrNeutered === 'true'
      ? 'Stérilisé'
      : 'Non stérilisé',
  ]

  const order: Array<AnimalInsightsDtoType> = ['info', 'error', 'warning']
  const insights = adoptionAttempt.insights || []
  const isSuspended =
    adoptionAttempt.status === AdoptionAttemptDtoStatus.SuspendedByCustomer ||
    adoptionAttempt.status === AdoptionAttemptDtoStatus.SuspendedByUser
  const statusLabel = getAdoptionAttemptStatus(adoptionAttempt)
  const isDone = adoptionAttempt.archived === true
  const isWaitingAdopter = statusLabel === "En attente de l'adoptant"
  const statusColor = isSuspended ? '#BC8076' : isDone ? '#5D9167' : isWaitingAdopter ? '#7A8197' : '#F3AC00'

  return (
    <Card
      sx={{
        mb: 2,
        cursor: 'pointer',
        borderRadius: 4,
        border: '1px solid rgba(228, 232, 242, 0.9)',
        boxShadow: '0 1px 2px rgba(32, 56, 100, 0.04)',
        backgroundColor: 'rgba(255,255,255,0.82)',
        '&:hover': { boxShadow: '0 10px 22px rgba(32, 56, 100, 0.08)' },
      }}
      onClick={() => navigate(`./?tab=${tab}&attemptId=${adoptionAttempt.id}`)}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1.75,
          }}
        >
          {/* Animal Details */}
          {animal && (
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
              <Avatar
                alt={animal.name}
                src={profileImageKey && getAnimalProfilePhotoSrc(profileImageKey)}
                sx={{ width: 52, height: 52, border: '1px solid rgba(228, 232, 242, 0.9)' }}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, minWidth: 0 }}>
                  <Typography
                    fontSize={17}
                    color={colors.cello}
                    fontWeight={800}
                    sx={{
                      lineHeight: 1.1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={animal?.name}
                  >
                    {animal?.name}
                  </Typography>
                  <HtmlTooltip
                    onClick={(e) => e.stopPropagation()}
                    title={
                      <React.Fragment>
                        {insights
                          .sort((a, b) => {
                            const indexA = order.indexOf(a.type)
                            const indexB = order.indexOf(b.type)
                            return indexA - indexB
                          })
                          .map((insight, index) => (
                            <InfoBox key={index} messageType={insight.type} content={insight.message} sx={{ mb: 1 }} />
                          ))}
                      </React.Fragment>
                    }
                  >
                    <Box sx={{ transform: 'scale(0.9)', transformOrigin: 'left center' }}>
                      <AvatarGroup>
                        {insights.length !== 0 ? (
                          <CircleWithNumber messageType="warning" number={insights.length} />
                        ) : (
                          <CircleWithNumber messageType="disabled" />
                        )}
                      </AvatarGroup>
                    </Box>
                  </HtmlTooltip>
                </Box>
                <Typography fontSize={13} color="#7A8197" sx={{ mt: 0.15, fontWeight: 400, lineHeight: 1.35 }}>
                  {animalProperties.filter(Boolean).join(', ')}
                </Typography>
                <Link
                  to={`/animaux/${animal?.id}`}
                  style={{ fontWeight: 700, color: blueSky, fontSize: '0.78rem', marginTop: 4 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  Voir le profil
                </Link>
                {guestCheckout && <GuestAdoptionChip sx={{ mt: 0.5, alignSelf: 'flex-start' }} />}
              </Box>
            </Box>
          )}

          {/* Copy Link Button */}
          {adoptionAttempt.paymentLink && !adoptionAttempt.paymentDone && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={(e) => {
                e.stopPropagation()
                handleCopyLink()
              }}
              sx={{
                ...addSecondaryFabSx,
                minHeight: 36,
                px: 2.1,
                textTransform: 'none',
                fontWeight: 700,
              }}
            >
              Copier le lien de paiement
            </Button>
          )}

          {/* Guest adoption: copy the payment link while it is awaiting payment */}
          {guestCheckout && !guestCheckout.paidAt && !isGuestCheckoutExpired(guestCheckout) && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={(e) => {
                e.stopPropagation()
                handleCopyGuestCheckoutLink()
              }}
              sx={{
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: 'bold',
                padding: '8px 16px',
              }}
            >
              Copier le lien de paiement
            </Button>
          )}

          {/* Adoption Status and Copy Link */}
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                alignSelf: 'center',
              }}
            >
              <Box
                component="span"
                sx={getSoftChipSx(statusColor, {
                  height: 30,
                  fontSize: 12.5,
                  labelWeight: 600,
                  emphasis: true,
                  maxWidth: 260,
                })}
              >
                <Typography
                  sx={{
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: colors.cello,
                    px: 1.35,
                    lineHeight: '30px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {statusLabel}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}
